/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import type { UserInfo } from '../../types';

type CustomerServiceIdentity = Pick<UserInfo, 'uid' | 'nickName' | 'name' | 'phone'>;

const toText = (value: unknown) => String(value || '').trim();
const LOCAL_CUSTOMER_SERVICE_ORIGIN = 'http://localhost:20108';
const joinCustomerServicePath = (basePath: string, suffix: string) =>
  `${basePath.replace(/\/+$/, '')}/${suffix.replace(/^\/+/, '')}`;

const isLocalDevHost = (hostname: string) => {
  const normalized = String(hostname || '').trim().toLowerCase();
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '0.0.0.0' ||
    normalized === '[::1]'
  );
};

const resolveCustomerServiceSiteUrl = (value: string) => {
  if (!value) return '';

  const normalizedValue = toText(value);
  if (!normalizedValue) return '';

  let resolvedValue = normalizedValue;
  if (!/^https?:\/\//i.test(resolvedValue)) {
    if (typeof window === 'undefined') {
      return resolvedValue;
    }

    if (isLocalDevHost(window.location.hostname)) {
      resolvedValue = new URL(
        resolvedValue.startsWith('/') ? resolvedValue : `/${resolvedValue}`,
        LOCAL_CUSTOMER_SERVICE_ORIGIN
      ).toString();
    } else {
      const normalizedPath = resolvedValue.startsWith('/') ? resolvedValue : `/${resolvedValue}`;
      resolvedValue = new URL(normalizedPath, window.location.origin).toString();
    }
  }

  try {
    const url = new URL(resolvedValue, window.location.origin);
    url.pathname = url.pathname
      .replace(/\/chat\/mobile\/?$/i, '')
      .replace(/\/api\/mobile\/service\/unread\/?$/i, '')
      .replace(/\/+$/, '');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return resolvedValue;
  }
};

export const resolveCustomerServiceToken = (baseUrl: string, token?: string) => {
  const explicitToken = toText(token);
  if (explicitToken) return explicitToken;

  try {
    const url = new URL(toText(baseUrl), window.location.origin);
    return toText(url.searchParams.get('token'));
  } catch {
    return '';
  }
};

export const buildCustomerServiceUnreadCountUrl = (
  unreadEndpoint: string,
  fallbackBaseUrl?: string
) => {
  const normalizedEndpoint = toText(unreadEndpoint);
  if (normalizedEndpoint) {
    try {
      return new URL(normalizedEndpoint, window.location.origin).toString();
    } catch {
      return normalizedEndpoint;
    }
  }

  const normalizedBaseUrl = toText(fallbackBaseUrl);
  if (!normalizedBaseUrl) return '';
  const resolvedBaseUrl = resolveCustomerServiceSiteUrl(normalizedBaseUrl);
  if (!resolvedBaseUrl) return '';

  try {
    const url = new URL(resolvedBaseUrl, window.location.origin);
    url.pathname = joinCustomerServicePath(url.pathname || '', '/api/mobile/service/unread');
    return url.toString();
  } catch {
    return '';
  }
};

export const buildCustomerServiceUrl = (
  baseUrl: string,
  user?: CustomerServiceIdentity | null,
  token?: string
) => {
  const normalizedBaseUrl = toText(baseUrl);
  if (!normalizedBaseUrl) return '';
  const resolvedBaseUrl = resolveCustomerServiceSiteUrl(normalizedBaseUrl);
  if (!resolvedBaseUrl) return '';

  try {
    const url = new URL(resolvedBaseUrl, window.location.origin);
    const resolvedToken = resolveCustomerServiceToken(baseUrl, token);
    const uid = toText(user?.uid);
    const nickName = toText(user?.nickName) || toText(user?.name);
    const phone = toText(user?.phone);
    url.pathname = joinCustomerServicePath(url.pathname || '', '/chat/mobile');

    if (resolvedToken) {
      url.searchParams.set('token', resolvedToken);
    }
    if (uid) {
      url.searchParams.set('uid', uid);
    }
    if (nickName) {
      url.searchParams.set('nickName', nickName);
    }
    if (phone) {
      url.searchParams.set('phone', phone);
    }

    return url.toString();
  } catch {
    return resolvedBaseUrl;
  }
};
